import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useNotify, useRefresh } from 'react-admin';

import { deleteReceipt } from './receiptApi';

type Props = {
    receiptId: number | null;
    onClose: () => void;
};

export const ReceiptDeleteDialog = ({ receiptId, onClose }: Props) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const open = receiptId != null;
    const hasReason = reason.trim().length > 0;

    const close = () => {
        if (busy) return;
        setReason('');
        onClose();
    };

    const submit = async () => {
        if (receiptId == null) return;
        const trimmed = reason.trim();
        if (!trimmed) {
            notify('Enter a reason for deleting this receipt.', {
                type: 'warning',
            });
            return;
        }
        setBusy(true);
        try {
            await deleteReceipt(receiptId, trimmed);
            notify('Receipt deleted', { type: 'success' });
            setReason('');
            onClose();
            refresh();
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Delete failed',
                { type: 'error' }
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>Delete receipt</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Reason"
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    disabled={busy}
                    helperText="A reason is required before you can delete."
                    error={!hasReason && reason.length > 0}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>
                    Cancel
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={() => void submit()}
                    disabled={busy || !hasReason}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
};
